import { Component, OnInit, OnDestroy, Input, Output, ViewChild, EventEmitter, OnChanges, SimpleChanges, ViewEncapsulation, input } from '@angular/core';
import { Subscription, pipe, finalize, concatMap, Subject, of } from 'rxjs';

import { environment } from 'src/environments/environment';

import { MessageService } from 'primeng/api';
import { map, filter, switchMap, debounceTime, distinctUntilChanged, tap } from 'rxjs/operators';
import { SecurityGroups, SecurityGroupMembers } from 'src/app/shared/securitygroups';
import { ClientApiService } from 'src/app/services/client-api.service';
import { SecureApiService } from 'src/app/services/secure-api.service';
import { HelperServerApiServiceService } from 'src/app/services/helper-server-api-service.service';
import { PerListEntry, } from 'src/app/shared/per-list-entry';
import { KeycloakUser } from 'src/app/shared/keycloak-user';
/** `KeycloakUser` extended with a `disabled` flag derived from the Keycloak `enabled` field. */
class KeycloakUsersList extends KeycloakUser {
  disabled?: boolean;
}

/**
 * Reusable Keycloak user selector backed by `HelperServerApiServiceService`, `ClientApiService`,
 * and `SecureApiService`.
 *
 * Three Subject pipelines run after init:
 * - `load$` — debounced `switchMap(getPerListEntry(SGMPERSID))` — resolves the DB person record
 *   linked to the current `userAccessGroupInfo` and stores it in `selectedDbPerson`.
 * - `area$` — debounced `map` — writes the selected Keycloak user's `id` onto
 *   `userAccessGroupInfo.SGMLOGIN` and emits `userUpdated`.
 * - `person$` — debounced `map` — matches a `PerListEntry` by email against the loaded Keycloak
 *   user list; on match, writes the Keycloak `id` to `SGMLOGIN`, emits `userUpdated`, and also
 *   pushes to `area$`.
 *
 * On init `_reloadComponentData()` fetches the full Keycloak user list (with `disabled` flags) and
 * calls `selectRow()` to pre-select the user whose `id` matches `userAccessGroupInfo.SGMLOGIN`.
 *
 * Action buttons: `onUserAddButton` creates a new Keycloak user via `postObservableKeycloakUser`;
 * `onUpdateUserButton` patches the existing user; `onPasswordSetButton` opens a password dialog;
 * `onUserPasswordSet` applies the new password via `putObservableKeycloakUser`.
 *
 * **Output:** `userUpdated` emits the mutated `SecurityGroupMembers` object on user selection or CRUD.
 */
@Component({
  selector: 'app-keycloakuserselector',
  templateUrl: './keycloakuserselector.component.html',
  styleUrl: './keycloakuserselector.component.css',
  encapsulation: ViewEncapsulation.None
})
export class KeycloakuserselectorComponent implements OnInit, OnChanges, OnDestroy {

  _loading: boolean;

  /** The security-group member record being edited; `SGMLOGIN` is written on user selection/add. */
  @Input() userAccessGroupInfo: SecurityGroupMembers = null;
  /** Emits the mutated `SecurityGroupMembers` after any user selection or CRUD operation. */
  @Output() public userUpdated: EventEmitter<SecurityGroupMembers> = new EventEmitter();

  /** When set, the component tries to auto-match the person's email against the Keycloak user list. */
  @Input() personToSearch: PerListEntry = null;

  /** Full Keycloak user list with `disabled` flags derived from the Keycloak `enabled` field. */
  keycloakUsers: KeycloakUsersList[] = [];

  /** Currently selected Keycloak user in the dropdown. */
  selectedKcUser: KeycloakUsersList = null;

  /** The DB person record resolved for `userAccessGroupInfo.SGMPERSID`. */
  selectedDbPerson: PerListEntry = null;

  _disabled: boolean = false;
  /** Placeholder text shown inside the dropdown when nothing is selected. */
  @Input() placeholder = 'Выбрать пользователя...';
  /** Label text displayed above the dropdown. */
  @Input() labelKey = 'Пользователи';
  /** When `true`, the dropdown is rendered as read-only. */
  @Input() get disabled(): boolean { return this._disabled; }
  set disabled(b: boolean) { this._disabled = b; }

  _showLabel: boolean = false;
  /** When `false`, the label above the dropdown is hidden. */
  @Input() get showLabel(): boolean { return this._showLabel; }
  set showLabel(b: boolean) { this._showLabel = b; }

  doShowAddUserButton: boolean = true;

  productGlobalCategories: string[] = [];
  productCategory: string = '';

  /** Pushes `userAccessGroupInfo` changes to trigger DB-person resolution. */
  private load$ = new Subject<SecurityGroupMembers>();
  /** Pushes a selected `KeycloakUser` to write `SGMLOGIN` and emit `userUpdated`. */
  private area$ = new Subject<KeycloakUser>();
  /** Pushes a `PerListEntry` to trigger email-based Keycloak user matching. */
  private person$ = new Subject<PerListEntry>();

  private _getCategoryQuery: Subscription = null;
  private _selCategoryQuery: Subscription = null;
  private _gelCategoryQuery: Subscription = null;
  private _personQuery: Subscription = null;

  /** `true` when a Keycloak user is currently selected in the dropdown. */
  public get userExists(): boolean {
    return (this.selectedKcUser != null);
  }

  /** `true` when `userAccessGroupInfo.SGMLOGIN` matches a loaded Keycloak user's `id`. */
  public get thisLoginExists(): boolean {
    return (this.userAccessGroupInfo != null) &&
      (this.userAccessGroupInfo.SGMLOGIN != null) &&
      (this.keycloakUsers != null) &&
      (this.keycloakUsers.find(u => u.id == this.userAccessGroupInfo.SGMLOGIN) != null)
      ;
  }

  showUpdateUserButton: boolean = true;
  showPasswordSetButton: boolean = true;
  openPasswordSetDialog: boolean = false;
  userPasswordText: string = '';
  enablePasswordDialog: boolean = true;

  constructor(
    private clientApi: ClientApiService,
    private secureApi: SecureApiService,
    private helperApi: HelperServerApiServiceService,
  ) { }

  /** Resolves `selectedKcUser` from `keycloakUsers` by matching `userAccessGroupInfo.SGMLOGIN`. */
  private selectRow(): void {
    this.selectedKcUser = null;
    if (this.userAccessGroupInfo && this.userAccessGroupInfo.SGMLOGIN && this.keycloakUsers && (this.keycloakUsers.length > 0)) {
      this.selectedKcUser = this.keycloakUsers.find(u => u.id == this.userAccessGroupInfo.SGMLOGIN);
      environment.production || console.log('*** KeycloakuserselectorComponent selectRow: selected user: ' + JSON.stringify(this.selectedKcUser));
    }
  }

  /** Fetches the full Keycloak user list, maps `disabled` from `enabled`, then calls `selectRow`. */
  private _reloadComponentData(): void {
    this._loading = true;
    this._getCategoryQuery =
      this.helperApi.getObservableKeycloakUsersList()
        .pipe(
          //tap(a => environment.production || console.log('*** KeycloakuserselectorComponent getObservableKeycloakUsersList: ' + JSON.stringify(a))),
          //tap(a => environment.production || console.table(a)),

          map(data => this.keycloakUsers = data.map(user => ({ ...user, disabled: !user.enabled }))),

          finalize(() => {
            this._loading = false;
            this.selectRow();
          })
        )
        .subscribe();
  }

  /** Pushes `userAccessGroupInfo` onto `load$` and `personToSearch` onto `person$` on input changes. */
  ngOnChanges(changes: SimpleChanges) {
    //console.log('*** KeycloakuserselectorComponent ngOnChanges');
    for (let propName in changes) {
      let change = changes[propName];

      console.log('*** KeycloakuserselectorComponent ngOnChanges (actual): ' + propName + ' ' + JSON.stringify(change.currentValue));
      //console.log('*** KeycloakuserselectorComponent ngOnChanges (was):    ' + propName + ' ' + JSON.stringify(change.previousValue));

      switch (propName) {
        case 'userAccessGroupInfo':
          this.load$.next(change.currentValue);
          break;

        case 'personToSearch':
          this.person$.next(change.currentValue);
          break;
      }
    }
  }

  /** Unsubscribes all active pipeline subscriptions to prevent memory leaks. */
  ngOnDestroy(): void {
    if (this._getCategoryQuery) { this._getCategoryQuery.unsubscribe(); }
    if (this._selCategoryQuery) { this._selCategoryQuery.unsubscribe(); }
    if (this._gelCategoryQuery) { this._gelCategoryQuery.unsubscribe(); }
    if (this._personQuery) { this._personQuery.unsubscribe(); }
  }

  /** Sets up all three Subject pipelines (`load$`, `area$`, `person$`), loads the Keycloak user list. */
  ngOnInit(): void {
    console.log('*** KeycloakuserselectorComponent ngOnInit: ');

    this._selCategoryQuery = this.load$
      .pipe(

        debounceTime(50),
        distinctUntilChanged(),

        tap(() => this._loading = true),

        switchMap(uag => this.clientApi.getPerListEntry(uag.SGMPERSID).pipe(
          filter(person => (person != null) && (person.length > 0)),
          map(person => person[0]),

          map(person => {
            this.selectedDbPerson = person;
            uag.personMap = person;
          }),

          finalize(() => {
            this._loading = false;
          })
        ))
      )
      .subscribe();

    this._gelCategoryQuery = this.area$
      .pipe(

        debounceTime(150),
        distinctUntilChanged(),

        map(user => {
          if (this.userAccessGroupInfo) {
            this.userAccessGroupInfo.SGMLOGIN = user.id;
            this.userUpdated.emit(this.userAccessGroupInfo);
          }
        }),
      )
      .subscribe();

    this._personQuery = this.person$.pipe(

      debounceTime(150),
      distinctUntilChanged(),
      filter(person => (person != null) && (person.PERSONEMAIL != null)),

      map(person => {
        this.selectedKcUser = this.keycloakUsers.find(u => u.email.trim().toUpperCase() == person.PERSONEMAIL.trim().toUpperCase());
        environment.production || console.log('*** KeycloakuserselectorComponent onChangeUser: this.person$: ' + JSON.stringify(this.selectedKcUser));

        if (this.selectedKcUser) {
          if (this.userAccessGroupInfo) {
            this.userAccessGroupInfo.SGMLOGIN = this.selectedKcUser.id;
            this.userUpdated.emit(this.userAccessGroupInfo);
          }
          this.area$.next(this.selectedKcUser);
        }
      })
    )
      .subscribe();

    this._reloadComponentData();
  }

  /** Handles dropdown `(onChange)` — pushes `selectedKcUser` onto `area$` to write `SGMLOGIN` and emit. */
  onChangeUser(e) {
    environment.production || console.log('*** KeycloakuserselectorComponent onChangeUser: ' + JSON.stringify(e));
    //this.selectedKcUser = this.keycloakUsers.find(u => u.id = e.value.id);
    if (this.selectedKcUser) {
      environment.production || console.log('*** KeycloakuserselectorComponent onChangeUser: selected user: ' + JSON.stringify(this.selectedKcUser));

      this.area$.next(this.selectedKcUser);
    }
  };

  /** Handles dropdown `(onClick)` — currently a no-op. */
  onClickUser(e: KeycloakUser) {
    environment.production || console.log('*** KeycloakuserselectorComponent onClickUser: ' + JSON.stringify(e));
  };

  /**
   * Creates a new Keycloak user from `userAccessGroupInfo` via `postObservableKeycloakUser`,
   * then writes the returned `userId` to `SGMLOGIN`, emits `userUpdated`, and reloads the list.
   */
  onUserAddButton(e: KeycloakUser) {
    environment.production || console.log('*** KeycloakuserselectorComponent onUserAddButton e: ' + JSON.stringify(e));
    environment.production || console.log('*** KeycloakuserselectorComponent onUserAddButton u: ' + JSON.stringify(this.userAccessGroupInfo));

    //this.enablePasswordDialog = true;

    // After creating a user, assign the 'user' role:
    //
    //this.postObservableKeycloakUser(uag).pipe(
    //  concatMap(response => {
    //    const userId = response.id; // or extract from Location header
    //    return this.assignRoleToUser(userId, 'user');
    //  })
    //).subscribe();

    // After creating a user, assign them to a group:
    //
    //this.postObservableKeycloakUser(uag).pipe(
    //  concatMap(response => {
    //    // Extract user ID from response or Location header
    //    const userId = response.id; // or parse from headers
    //    const groupId = 'your-group-id-here';
    //    return this.putObservableKeycloakUserToGroup(userId, groupId);
    //  })
    //).subscribe(
    //  () => console.log('User assigned to group successfully'),
    //  error => console.error('Error assigning user to group:', error)
    //);

    this.helperApi.postObservableKeycloakUser(this.userAccessGroupInfo).subscribe({
      next: (response) => {
        environment.production || console.log('*** KeycloakuserselectorComponent onUserAddButton: user added successfully: ' + JSON.stringify(response));
        this.userAccessGroupInfo.SGMLOGIN = response.userId;
        this.userUpdated.emit(this.userAccessGroupInfo);
        this._reloadComponentData();
      },
      error: (error) => {
        environment.production || console.log('*** KeycloakuserselectorComponent onUserAddButton: error adding user: ' + JSON.stringify(error));
      },

      complete: () => {
      }
    });
  }

  /** Patches the existing Keycloak user record via `putObservableKeycloakUser`. */
  onUpdateUserButton(e: KeycloakUser) {
    environment.production || console.log('*** KeycloakuserselectorComponent onUpdateUserButton e: ' + JSON.stringify(e));
    environment.production || console.log('*** KeycloakuserselectorComponent onUpdateUserButton u: ' + JSON.stringify(this.userAccessGroupInfo));
    this.helperApi.putObservableKeycloakUser(this.userAccessGroupInfo).subscribe({
      next: (response) => {
        environment.production || console.log('*** KeycloakuserselectorComponent onUpdateUserButton: user updated successfully: ' + JSON.stringify(response));
      },
      error: (error) => {
        environment.production || console.log('*** KeycloakuserselectorComponent onUpdateUserButton: error updating user: ' + JSON.stringify(error));
      },
    });
  }

  /** Opens the password-set dialog by setting `enablePasswordDialog = true`. */
  onPasswordSetButton(e: KeycloakUser) {
    environment.production || console.log('*** KeycloakuserselectorComponent onPasswordSetButton e: ' + JSON.stringify(e));
    environment.production || console.log('*** KeycloakuserselectorComponent onPasswordSetButton u: ' + JSON.stringify(this.userAccessGroupInfo));

    // Here we need to call dialog windows to set password
    // And on confirmation call helperApi to set password

    this.enablePasswordDialog = true;
  }

  /**
   * Writes `newPassword` to `userAccessGroupInfo.SGMPASSWD` and calls `putObservableKeycloakUser`
   * to apply the new password in Keycloak.
   */
  onUserPasswordSet(newPassword: string) {
    environment.production || console.log('*** KeycloakuserselectorComponent onUserPasswordSet newPassword: ' + JSON.stringify(newPassword));

    this.userAccessGroupInfo.SGMPASSWD = newPassword;

    this.helperApi.putObservableKeycloakUser(this.userAccessGroupInfo).subscribe({
      next: (response) => {
        environment.production || console.log('*** KeycloakuserselectorComponent onUserPasswordSet: user updated successfully: ' + JSON.stringify(response));
      },
      error: (error) => {
        environment.production || console.log('*** KeycloakuserselectorComponent onUserPasswordSet: error updating user: ' + JSON.stringify(error));
      }
    });
  }
}
